import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardHeader from '../../components/dashboard/DashboardHeader';
import DashboardFooter from '../../components/dashboard/DashboardFooter';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 24px;
  background: #ededed;
  border-radius: 12px 12px 0 0;
  padding: 24px 32px 16px 32px;
  color: #444;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    padding: 20px 20px 12px 20px;
  }
`;
const ControlsRow = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 24px;
  align-items: flex-end;
  flex-wrap: wrap;
`;
const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Label = styled.label`
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 2px;
`;
const Select = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
`;


const TableWrapper = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow-x: auto;
  margin-top: 24px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
`;
const Th = styled.th`
  background: #f7f7f7;
  font-weight: 700;
  padding: 12px 8px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
  white-space: nowrap;
`;
const Td = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
`;


const SearchInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  font-size: 14px;
`;

const TreeContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 24px;
  margin-top: 24px;
  overflow-x: auto;
  overflow-y: auto;
  max-width: 100%;
`;

const TreeGrid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  min-height: 400px;
  overflow-x: auto;
  min-width: max-content;
  width: 100%;
`;

const TreeNode = styled.div`
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  position: relative;
  min-height: 120px;
  width: 280px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 240px;
    min-height: 100px;
  }
  
  @media (max-width: 480px) {
    width: 200px;
    padding: 12px;
  }
`;

const TreeLevel = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 60px;
  margin-bottom: 60px;
  position: relative;
  min-width: 100%;
  
  @media (max-width: 768px) {
    gap: 30px;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  
  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const TreeNodeName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
`;

const TreeNodeId = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
`;

const TreeNodeDetails = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
`;

const LevelCell = styled.div`
  display: flex;
  align-items: center;
  min-width: 60px;
  padding-left: 12px;
`;

const ActivityTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 110px;
`;

const ACTIVITY_STATUS_META = {
  active: {
    label: 'Активен',
    color: '#2E7D32',
    background: 'rgba(46, 125, 50, 0.12)'
  },
  pending: {
    label: 'Требуется покупка',
    color: '#EF6C00',
    background: 'rgba(239, 108, 0, 0.12)'
  },
  inactive: {
    label: 'Не активен',
    color: '#C62828',
    background: 'rgba(198, 40, 40, 0.12)'
  },
  disabled: {
    label: 'Отключено',
    color: '#607D8B',
    background: 'rgba(96, 125, 139, 0.12)'
  },
  default: {
    label: 'Нет данных',
    color: '#4B5563',
    background: 'rgba(107, 114, 128, 0.12)'
  }
};

const getActivityStatusMeta = (status) => {
  if (!status) {
    return ACTIVITY_STATUS_META.pending;
  }
  const key = status.toLowerCase();
  return ACTIVITY_STATUS_META[key] || ACTIVITY_STATUS_META.default;
};

// Пример вложенных данных (закомментировано, так как используются реальные данные)
/* const teamData = [
  {
    level: 0,
    id: '100102812',
    name: 'Бортник Александр Петрович',
    lo: 5250,
    online: 5250,
    go: 17500,
    activity: 'Активность',
    status: 'Партнер',
    qualification: 'Партнер',
    inviterId: '999999999',
    inviterName: 'Компания',
    children: [
      {
        level: 1,
        id: '100525154',
        name: 'Монетов Олег Владимирович',
        lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100102812', inviterName: 'Бортник Александр Петрович',
        children: [
          {
            level: 2,
            id: '100525155',
            name: 'Иванов Иван Иванович',
            lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100525154', inviterName: 'Монетов Олег Владимирович',
            children: [
              {
                level: 3,
                id: '100525156',
                name: 'Петров Петр Петрович',
                lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100525155', inviterName: 'Иванов Иван Иванович',
                children: [
                  {
                    level: 4,
                    id: '100525160',
                    name: 'Смирнов Алексей Алексеевич',
                    lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100525156', inviterName: 'Петров Петр Петрович',
                    children: [
                      {
                        level: 5,
                        id: '100525161',
                        name: 'Егоров Егор Егорович',
                        lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100525160', inviterName: 'Смирнов Алексей Алексеевич',
                        children: []
                      }
                    ]
                  }
                ]
              },
              {
                level: 3,
                id: '100525157',
                name: 'Сидоров Сидор Сидорович',
                lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100525155', inviterName: 'Иванов Иван Иванович',
                children: []
              }
            ]
          },
          {
            level: 2,
            id: '100525158',
            name: 'Кузнецов Николай Николаевич',
            lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100525154', inviterName: 'Монетов Олег Владимирович',
            children: [
              {
                level: 3,
                id: '100525159',
                name: 'Васильев Василий Васильевич',
                lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100525158', inviterName: 'Кузнецов Николай Николаевич',
                children: [
                  {
                    level: 4,
                    id: '100525162',
                    name: 'Дмитриев Дмитрий Дмитриевич',
                    lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100525159', inviterName: 'Васильев Василий Васильевич',
                    children: [
                      {
                        level: 5,
                        id: '100525163',
                        name: 'Зайцев Захар Захарович',
                        lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100525162', inviterName: 'Дмитриев Дмитрий Дмитриевич',
                        children: []
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        level: 1,
        id: '100629324',
        name: 'Бортник Александр Александрович',
        lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100102812', inviterName: 'Бортник Александр Петрович',
        children: [
          {
            level: 2,
            id: '100629325',
            name: 'Морозова Мария Сергеевна',
            lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100629324', inviterName: 'Бортник Александр Александрович',
            children: [
              {
                level: 3,
                id: '100629326',
                name: 'Григорьев Григорий Григорьевич',
                lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100629325', inviterName: 'Морозова Мария Сергеевна',
                children: [
                  {
                    level: 4,
                    id: '100629327',
                    name: 'Ковалев Константин Константинович',
                    lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100629326', inviterName: 'Григорьев Григорий Григорьевич',
                    children: [
                      {
                        level: 5,
                        id: '100629328',
                        name: 'Лебедев Леонид Леонидович',
                        lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100629327', inviterName: 'Ковалев Константин Константинович',
                        children: []
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        level: 1,
        id: '100791563',
        name: 'Спиридонова Олеся Александровна',
        lo: '', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100102812', inviterName: 'Бортник Александр Петрович',
        children: []
      },
      {
        level: 1,
        id: '100627674',
        name: 'Панфилова Мария Юрьевна',
        lo: '6600', online: '', go: '', activity: '', status: 'Партнер', qualification: 'Партнер', inviterId: '100102812', inviterName: 'Бортник Александр Петрович',
        children: []
      }
    ]
  }
]; */

const formatVolume = (value) => {
  if (value === null || value === undefined) return '—';
  const number = Number(value);
  if (Number.isNaN(number)) return '—';
  if (number === 0) return '0';
  return number.toLocaleString('ru-RU');
};

const formatText = (value, fallback = '—') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return value;
};

function renderRows(data, maxLevel, search, expandedRows, toggleRow, parentLevel = 0) {
  return data.flatMap(row => {
    // Показываем уровни 1..maxLevel включительно (как в админке)
    if (row.level > maxLevel) return [];
    
    // Проверяем поиск
    const matchesSearch = !search || 
      row.name.toLowerCase().includes(search.toLowerCase()) ||
      row.id.toString().includes(search);
    
    // Рекурсивно проверяем детей
    const childRows = row.children && row.children.length > 0 
      ? renderRows(row.children, maxLevel, search, expandedRows, toggleRow, row.level + 1)
      : [];
    
    // Показываем элемент если он соответствует поиску или имеет подходящих детей
    if (!matchesSearch && childRows.length === 0) {
      return [];
    }
    
    const hasChildren = row.children && row.children.length > 0;
    const isExpanded = expandedRows[row.id];
    const activityMeta = getActivityStatusMeta(row.activityStatus);
    const isPartner = row.userStatus === 'partner';
    
    // Определяем фон строки: голубой для партнеров, иначе чередующийся
    const rowBackground = isPartner 
      ? '#E3F2FD' 
      : (row.level % 2 ? '#f7faf7' : '#fff');
    
    const currentRow = (
      <tr key={row.id} style={{background: rowBackground}}>
        <Td>
          <LevelCell>
            {hasChildren && (
              <span style={{cursor: 'pointer', marginRight: 6}} onClick={() => toggleRow(row.id)}>
                {isExpanded ? <FaChevronDown size={14} /> : <FaChevronRight size={14} />}
              </span>
            )}
            <span>{row.level}</span>
          </LevelCell>
        </Td>
        <Td>{row.id}</Td>
        <Td style={{paddingLeft: `${row.level * 20 + 8}px`}}>{row.name}</Td>
        <Td>{formatVolume(row.lo)}</Td>
        <Td>{formatVolume(row.go)}</Td>
        <Td>
          <ActivityTag style={{ background: activityMeta.background, color: activityMeta.color }}>
            {row.activityLabel || activityMeta.label}
          </ActivityTag>
        </Td>
        <Td>{formatText(row.qualification)}</Td>
        <Td>{row.referralsCount || 0}</Td>
        <Td>{row.inviterId || '—'}</Td>
        <Td>{formatText(row.inviterName)}</Td>
      </tr>
    );
    
    // Возвращаем текущую строку и дочерние (если раскрыты)
    return [
      currentRow,
      ...(hasChildren && isExpanded ? childRows : [])
    ];
  });
}

const renderTreeWithProperLines = (data, maxLevel, search) => {
  const buildTreeLevel = (nodes, level = 0) => {
    if (level >= maxLevel) return null; /* maxLevel=10 → глубины 0..9 */
    
    // Фильтруем узлы с учетом поиска
    const filteredNodes = nodes.filter(row => {
      if (!search) return true;
      
      const matchesSearch = row.name.toLowerCase().includes(search.toLowerCase()) ||
        row.id.toString().includes(search);
      
      // Также проверяем, есть ли подходящие дети
      const hasMatchingChildren = row.children && row.children.some(child => 
        child.name.toLowerCase().includes(search.toLowerCase()) ||
        child.id.toString().includes(search) ||
        hasDeepMatch(child, search)
      );
      
      return matchesSearch || hasMatchingChildren;
    });
    
    if (filteredNodes.length === 0) return null;
    
    return (
      <TreeLevel key={level} style={{ 
        gap: level > 2 ? '30px' : '60px',
        flexWrap: level > 2 ? 'wrap' : 'nowrap',
        justifyContent: level > 2 ? 'flex-start' : 'center'
      }}>
        {filteredNodes.map((node, index) => (
          <div key={node.id} style={{ position: 'relative' }}>
            <TreeNode>
              <TreeNodeName>
                {'•'.repeat(level + 1)} {node.name}
              </TreeNodeName>
              <TreeNodeId>{node.id}</TreeNodeId>
              <TreeNodeDetails>
                Уровень: {node.level} | Квалификация: {formatText(node.qualification)}
              </TreeNodeDetails>
              <TreeNodeDetails>
                ЛО: {formatVolume(node.lo)} | ГО: {formatVolume(node.go)}
              </TreeNodeDetails>
              {(() => {
                const meta = getActivityStatusMeta(node.activityStatus);
                return (
                  <TreeNodeDetails>
                    Активность:{' '}
                    <ActivityTag style={{ background: meta.background, color: meta.color }}>
                      {node.activityLabel || meta.label}
                    </ActivityTag>
                  </TreeNodeDetails>
                );
              })()}
            </TreeNode>
            {node.children && node.children.length > 0 && level < maxLevel - 1 && (
              <div style={{ marginTop: '40px', position: 'relative' }}>
                {buildTreeLevel(node.children, level + 1)}
              </div>
            )}
          </div>
        ))}
      </TreeLevel>
    );
  };
  
  // Вспомогательная функция для глубокого поиска
  const hasDeepMatch = (node, search) => {
    if (!node.children) return false;
    
    return node.children.some(child => 
      child.name.toLowerCase().includes(search.toLowerCase()) ||
      child.id.toString().includes(search) ||
      hasDeepMatch(child, search)
    );
  };
  
  return buildTreeLevel(data);
};

const getCurrentMskPeriod = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit'
  }).formatToParts(new Date());
  return {
    year: parseInt(parts.find((p) => p.type === 'year').value, 10),
    month: parseInt(parts.find((p) => p.type === 'month').value, 10)
  };
};

const buildPeriodOptions = (count = 12) => {
  const options = [];
  let { year, month } = getCurrentMskPeriod();
  for (let i = 0; i < count; i += 1) {
    const label = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('ru-RU', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
    options.push({ year, month, label, value: `${year}-${String(month).padStart(2, '0')}` });
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }
  return options;
};

const Team = () => {
  const { isAuthenticated, user } = useAuth();
  
  const defaultMaxLevel = 10;
  const allowedMaxLevel = 10;
  const periodOptions = React.useMemo(() => buildPeriodOptions(12), []);
  const defaultPeriod = periodOptions[0]?.value || `${getCurrentMskPeriod().year}-${String(getCurrentMskPeriod().month).padStart(2, '0')}`;
  
  const [period, setPeriod] = useState(defaultPeriod);
  const [maxLevel, setMaxLevel] = useState(defaultMaxLevel);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [viewMode, setViewMode] = useState('table'); // 'table' или 'tree'
  const [teamData, setTeamData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Обновляем maxLevel при изменении статуса пользователя
  React.useEffect(() => {
    if (maxLevel > allowedMaxLevel) {
      setMaxLevel(allowedMaxLevel);
    }
  }, [allowedMaxLevel, maxLevel]);

  // Загрузка данных команды
  useEffect(() => {
    const loadTeamData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const [periodYear, periodMonth] = period.split('-').map((v) => parseInt(v, 10));
        const response = await fetch(
          `/api/referrals/my-team?maxLevel=${maxLevel}&format=list&year=${periodYear}&month=${periodMonth}`,
          { headers }
        );
        if (response.ok) {
          const data = await response.json();
          
          // Преобразуем данные в формат для отображения
          const currentUserId = user?.id ? user.id.toString() : null;

          const formattedData = data.referrals.map(referral => {
            const personalVolume = Number(referral.personalVolume) || 0;
            const groupVolume = Number(referral.groupVolume) || 0;
            const referrerId = referral.referrerId ? referral.referrerId.toString() : null;
            const fallbackName = [referral.lastName, referral.firstName].filter(Boolean).join(' ');
            const referrerFullName = referral.referrerFullName 
              || (referral.referrerLastName && referral.referrerFirstName
                ? `${referral.referrerLastName} ${referral.referrerFirstName}`
                : null);

            const activityStatus = (referral.activityStatus || 'pending').toLowerCase();
            const activityMeta = getActivityStatusMeta(activityStatus);

            return {
              level: referral.level,
              id: referral.id ? referral.id.toString() : '',
              name: referral.fullName || fallbackName || '—',
              lo: personalVolume,
              go: groupVolume,
              activityStatus,
              activityLabel: referral.activityStatusLabel || activityMeta.label,
              qualification: referral.qualification || 'Без статуса',
              inviterId: referrerId || null,
              inviterName: referrerFullName || null,
              referrerId: referrerId,
              referrerFullName: referrerFullName,
              children: [],
              registrationDate: referral.registrationDate,
              path: referral.path || '',
              userStatus: referral.userStatus || 'sport',
              referralsCount: referral.referralsCount || 0
            };
          });

          const itemsById = new Map();
          formattedData.forEach(item => {
            if (item.id) {
              itemsById.set(item.id, item);
            }
          });

          formattedData.forEach(item => {
            if (!item.inviterName && item.referrerId) {
              const parent = itemsById.get(item.referrerId);
              if (parent && parent.name) {
                item.inviterName = parent.name;
              }
            }

            if (!item.inviterName && currentUserId && item.referrerId === currentUserId) {
              const currentUserName = [user?.lastName, user?.firstName].filter(Boolean).join(' ');
              item.inviterName = currentUserName || '—';
            }

            if (!item.inviterId && item.referrerId) {
              item.inviterId = item.referrerId;
            }

            if (!item.inviterName) {
              item.inviterName = '—';
            }
          });

          const hierarchyData = buildTeamHierarchy(formattedData, currentUserId);

          // #region agent log
          const countNodes = (items) => items.reduce((n, i) => n + 1 + (i.children ? countNodes(i.children) : 0), 0);
          const hiddenByLevelFilter = formattedData.filter((r) => r.level > maxLevel).length;
          fetch('http://127.0.0.1:7515/ingest/1206fec0-b699-4bb3-b443-a818513e7935',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'5c8af0'},body:JSON.stringify({sessionId:'5c8af0',hypothesisId:'B,C,D',location:'Team.js:loadTeamData',message:'team data loaded',data:{viewerId:currentUserId,apiTotal:data.totalCount,flatRows:formattedData.length,treeNodes:countNodes(hierarchyData),maxLevel,hiddenByLevelFilter,sampleIds:formattedData.slice(0,8).map(r=>r.id),has1311:formattedData.some(r=>r.id==='1311'),sampleVolumes:formattedData.filter(r=>['1307','1308','1311','1316'].includes(r.id)).map(r=>({id:r.id,lo:r.lo,go:r.go}))},timestamp:Date.now()})}).catch(()=>{});
          // #endregion

          setTeamData(hierarchyData);
          
          // Автоматически раскрываем все строки с детьми
          const newExpandedRows = {};
          const expandAll = (items) => {
            items.forEach(item => {
              if (item.children && item.children.length > 0) {
                newExpandedRows[item.id] = true;
                expandAll(item.children);
              }
            });
          };
          expandAll(hierarchyData);
          setExpandedRows(newExpandedRows);
        } else {
          setError('Ошибка загрузки данных команды');
        }
      } catch (error) {
        console.error('Error loading team data:', error);
        setError('Ошибка загрузки данных команды');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated()) {
      loadTeamData();
    }
  }, [maxLevel, period, isAuthenticated, user]);

  // При изменении maxLevel не раскрывать строки автоматически
  useEffect(() => {
    setExpandedRows({});
  }, [maxLevel, period]);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Иерархия по referred_by_id (корректно после переноса веток)
  const buildTeamHierarchy = (flatData, currentUserId) => {
    if (flatData.length === 0) return [];

    const viewerId = currentUserId ? String(currentUserId) : null;
    const itemsMap = {};
    flatData.forEach((item) => {
      itemsMap[item.id] = { ...item, children: [] };
    });

    const roots = [];
    flatData.forEach((item) => {
      const node = itemsMap[item.id];
      const parentId = item.referrerId ? String(item.referrerId) : null;

      if (!parentId || (viewerId && parentId === viewerId)) {
        roots.push(node);
      } else if (itemsMap[parentId]) {
        itemsMap[parentId].children.push(node);
      } else {
        roots.push(node);
      }
    });

    const sortNodes = (nodes) => {
      nodes.sort((a, b) => (a.level - b.level) || (Number(a.id) - Number(b.id)));
      nodes.forEach((n) => sortNodes(n.children));
    };
    sortNodes(roots);
    return roots;
  };

  // Если пользователь не авторизован, перенаправляем на главную
  if (!isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <DashboardHeader />
      <Container>
        <Title>Моя команда</Title>
        <ControlsRow>
          <ControlGroup>
            <Label htmlFor="period">Период (календарный месяц, МСК)</Label>
            <Select id="period" value={period} onChange={e => setPeriod(e.target.value)}>
              {periodOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </ControlGroup>
          <ControlGroup>
            <Label htmlFor="level">До уровня</Label>
            <Select 
              id="level" 
              value={maxLevel} 
              onChange={e => setMaxLevel(Math.min(Number(e.target.value), allowedMaxLevel))}
            >
              {[...Array(allowedMaxLevel)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </Select>
          </ControlGroup>
          <ControlGroup>
            <Label>Отображение</Label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '6px 0 0 6px',
                  background: viewMode === 'table' ? '#ff6b35' : '#fff',
                  color: viewMode === 'table' ? '#fff' : '#333',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onClick={() => setViewMode('table')}
              >
                Таблицей
              </button>
              <button
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '0 6px 6px 0',
                  background: viewMode === 'tree' ? '#ff6b35' : '#fff',
                  color: viewMode === 'tree' ? '#fff' : '#333',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onClick={() => setViewMode('tree')}
              >
                Деревом
              </button>
            </div>
          </ControlGroup>
          <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'flex-end', gap: 8}}>
            <SearchInput placeholder="Поиск..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </ControlsRow>
        {loading ? (
          <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
            Загрузка данных команды...
          </div>
        ) : error ? (
          <div style={{textAlign: 'center', padding: '40px', color: '#f44336'}}>
            {error}
          </div>
        ) : teamData.length === 0 ? (
          <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
            У вас пока нет рефералов. Поделитесь своей реферальной ссылкой, чтобы пригласить новых участников!
          </div>
        ) : viewMode === 'table' ? (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Уровень</Th>
                  <Th>ID</Th>
                  <Th>Ф.И.О.</Th>
                  <Th>ЛО</Th>
                  <Th>ГО</Th>
                  <Th>Активность</Th>
                  <Th>Квалификация</Th>
                  <Th>Количество рефералов</Th>
                  <Th>ID пригласившего</Th>
                  <Th>Ф.И.О. пригласившего</Th>
                </tr>
              </thead>
              <tbody>
                {renderRows(teamData, maxLevel, search, expandedRows, toggleRow)}
              </tbody>
            </Table>
          </TableWrapper>
        ) : (
          <TreeContainer>
            <TreeGrid>
              {renderTreeWithProperLines(teamData, maxLevel, search)}
            </TreeGrid>
          </TreeContainer>
        )}
      </Container>
      <DashboardFooter />
    </>
  );
};

export default Team; 